import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  Text,
  Box,
  Alert,
  AlertIcon,
  Textarea,
  Grid,
  GridItem,
  IconButton,
  Badge,
  Flex,
} from '@chakra-ui/react';

import { FaTrash } from 'react-icons/fa';

import { useColors } from './colors';

const CompetitorSettingsModal = ({ isOpen, onClose, onSave, currentSettings }) => {
  const { textColor, inputColor } = useColors();

  const [settings, setSettings] = useState({
    priorityCompetitors: [],
    excludedCompetitors: [],
  });

  const [newPriorityCompetitor, setNewPriorityCompetitor] = useState('');
  const [newExcludedCompetitor, setNewExcludedCompetitor] = useState('');

  // Load current settings when modal opens
  useEffect(() => {
    if (currentSettings && isOpen) {
      setSettings({
        priorityCompetitors: currentSettings.priorityCompetitors || [],
        excludedCompetitors: currentSettings.excludedCompetitors || [],
      });
    }
  }, [currentSettings, isOpen]);

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const addPriorityCompetitor = () => {
    if (
      newPriorityCompetitor.trim() &&
      !settings.priorityCompetitors.includes(newPriorityCompetitor.trim())
    ) {
      setSettings({
        ...settings,
        priorityCompetitors: [
          ...settings.priorityCompetitors,
          newPriorityCompetitor.trim(),
        ],
      });
      setNewPriorityCompetitor('');
    }
  };

  const removePriorityCompetitor = (competitor) => {
    setSettings({
      ...settings,
      priorityCompetitors: settings.priorityCompetitors.filter((c) => c !== competitor),
    });
  };

  const addExcludedCompetitor = () => {
    if (
      newExcludedCompetitor.trim() &&
      !settings.excludedCompetitors.includes(newExcludedCompetitor.trim())
    ) {
      setSettings({
        ...settings,
        excludedCompetitors: [
          ...settings.excludedCompetitors,
          newExcludedCompetitor.trim(),
        ],
      });
      setNewExcludedCompetitor('');
    }
  };

  const removeExcludedCompetitor = (competitor) => {
    setSettings({
      ...settings,
      excludedCompetitors: settings.excludedCompetitors.filter((c) => c !== competitor),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="1000px" maxH="90vh" borderRadius="xl" boxShadow="2xl">
        <ModalHeader color={textColor} fontSize="xl" fontWeight="600" pb={2}>
          Competitor Settings
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" maxH="calc(90vh - 140px)">
          <Grid templateColumns="1fr 1fr" gap={8}>
            {/* Left Column - Priority Competitors */}
            <GridItem>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={4}>
                    Priority Competitors
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    These competitors will be prioritized when selecting the best
                    competitor for pricing.
                  </Text>

                  <VStack spacing={4} align="stretch">
                    <HStack spacing={2}>
                      <Input
                        placeholder="Enter eBay seller username"
                        value={newPriorityCompetitor}
                        onChange={(e) => setNewPriorityCompetitor(e.target.value)}
                        color={inputColor}
                        borderRadius="md"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px blue.400',
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addPriorityCompetitor();
                          }
                        }}
                      />
                      <Button
                        onClick={addPriorityCompetitor}
                        colorScheme="blue"
                        size="sm"
                        isDisabled={!newPriorityCompetitor.trim()}
                        borderRadius="md"
                        fontWeight="500"
                        _disabled={{ opacity: 0.5 }}
                      >
                        Add
                      </Button>
                    </HStack>

                    <Box>
                      <Text fontSize="sm" color={textColor} mb={2} fontWeight="500">
                        Current Priority Competitors:
                      </Text>
                      <Textarea
                        value={settings.priorityCompetitors.join('\n')}
                        placeholder="Priority competitors will appear here..."
                        rows={6}
                        isReadOnly
                        color={inputColor}
                        bg="gray.50"
                        borderRadius="md"
                        borderColor="gray.200"
                        fontSize="sm"
                        _focus={{ borderColor: 'gray.300' }}
                      />
                    </Box>

                    {settings.priorityCompetitors.length > 0 && (
                      <Flex wrap="wrap" gap={2}>
                        {settings.priorityCompetitors.map((competitor, index) => (
                          <Badge
                            key={index}
                            colorScheme="blue"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="500"
                          >
                            <HStack spacing={1}>
                              <Text>{competitor}</Text>
                              <IconButton
                                icon={<FaTrash />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => removePriorityCompetitor(competitor)}
                                aria-label="Remove competitor"
                                _hover={{ bg: 'red.100' }}
                              />
                            </HStack>
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            {/* Right Column - Excluded Competitors */}
            <GridItem>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={4}>
                    Excluded Competitors
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    These competitors will be excluded from competitor analysis and
                    pricing calculations.
                  </Text>

                  <VStack spacing={4} align="stretch">
                    <HStack spacing={2}>
                      <Input
                        placeholder="Enter eBay seller username"
                        value={newExcludedCompetitor}
                        onChange={(e) => setNewExcludedCompetitor(e.target.value)}
                        color={inputColor}
                        borderRadius="md"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'red.300' }}
                        _focus={{
                          borderColor: 'red.400',
                          boxShadow: '0 0 0 1px red.400',
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addExcludedCompetitor();
                          }
                        }}
                      />
                      <Button
                        onClick={addExcludedCompetitor}
                        colorScheme="red"
                        size="sm"
                        isDisabled={!newExcludedCompetitor.trim()}
                        borderRadius="md"
                        fontWeight="500"
                        _disabled={{ opacity: 0.5 }}
                      >
                        Add
                      </Button>
                    </HStack>

                    <Box>
                      <Text fontSize="sm" color={textColor} mb={2} fontWeight="500">
                        Current Excluded Competitors:
                      </Text>
                      <Textarea
                        value={settings.excludedCompetitors.join('\n')}
                        placeholder="Excluded competitors will appear here..."
                        rows={6}
                        isReadOnly
                        color={inputColor}
                        bg="gray.50"
                        borderRadius="md"
                        borderColor="gray.200"
                        fontSize="sm"
                        _focus={{ borderColor: 'gray.300' }}
                      />
                    </Box>

                    {settings.excludedCompetitors.length > 0 && (
                      <Flex wrap="wrap" gap={2}>
                        {settings.excludedCompetitors.map((competitor, index) => (
                          <Badge
                            key={index}
                            colorScheme="red"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="500"
                          >
                            <HStack spacing={1}>
                              <Text>{competitor}</Text>
                              <IconButton
                                icon={<FaTrash />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => removeExcludedCompetitor(competitor)}
                                aria-label="Remove competitor"
                                _hover={{ bg: 'red.100' }}
                              />
                            </HStack>
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>
          </Grid>

          <Alert
            status="info"
            mt={8}
            borderRadius="md"
            bg="blue.50"
            borderColor="blue.200"
          >
            <AlertIcon color="blue.500" />
            <Box>
              <Text fontSize="sm" color="blue.700">
                Priority competitors are considered first when selecting the best
                competitor for pricing. Excluded competitors are completely ignored during
                competitor analysis.
              </Text>
            </Box>
          </Alert>
        </ModalBody>

        <ModalFooter pt={4} borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" mr={3} onClick={onClose} fontWeight="500">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            borderRadius="md"
            fontWeight="500"
            px={6}
          >
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CompetitorSettingsModal;
